import React, { FC } from 'react';
import * as Styled from './TicketDevice.styles';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import { HttpError, HttpStatusCode } from '@core/http';
import { CheckSerialParams, checkSerialSchema } from '@modules/ticket/model';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@shared/modules/translation/hooks';
import { Translations } from '@shared/modules/translation/locales';
import { Select, SelectOption } from '@styles/shared/form/Select';
import { getLinkPropsFromPrismicLink } from '@core/prismic';
import { useLayoutContext } from '@layout/Layout';
import CustomLink from '@shared/components/link/CustomLink';

function getLabelBuilder(error: O.Option<HttpError>, translations: Translations) {
  return pipe(
    error,
    O.map(err => {
      switch (err.status) {
        case HttpStatusCode.NOT_FOUND:
          return translations.error.serial.notFound;
        case HttpStatusCode.FORBIDDEN:
          return translations.error.serial.forbidden;
        default:
          return translations.error.message.default;
      }
    }),
    O.toNullable,
  );
}

function getLinkBuilder(error: O.Option<HttpError>, translations: Translations) {
  return pipe(
    error,
    O.fold(
      () => null,
      err => {
        if (err.status === HttpStatusCode.NOT_FOUND)
          return (
            <Styled.TicketDeviceButton as={CustomLink} to="contact" $format="secondary">
              {translations.tickets.contact}
            </Styled.TicketDeviceButton>
          );

        if (err.status === HttpStatusCode.FORBIDDEN)
          return (
            <Styled.TicketDeviceButton as={CustomLink} to="home" $format="secondary">
              {translations.tickets.home}
            </Styled.TicketDeviceButton>
          );

        return null;
      },
    ),
  );
}

interface TicketDeviceFormProps {
  loading: boolean;
  error: O.Option<HttpError>;
  onSubmit: (params: CheckSerialParams) => void;
  options: Array<SelectOption>;
}

const TicketDeviceForm: FC<TicketDeviceFormProps> = ({ loading, error, onSubmit, options }) => {
  const translations = useTranslations();
  const { layout } = useLayoutContext();

  const {
    watch,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CheckSerialParams>({
    resolver: zodResolver(checkSerialSchema),
    defaultValues: {
      equipmentSerialId: '',
    },
  });

  const handleChange = (value: string | null) => {
    if (value != null) {
      const isInSelectOptions = options.find(element => element.value === value);
      if (!isInSelectOptions) options.push({ value: value, label: value });

      setValue('equipmentSerialId', value ?? '');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Styled.TicketDeviceContent>
        <Styled.TicketDeviceInput label={translations.tickets.serial} htmlFor="serial">
          <Select
            id="serial"
            value={watch('equipmentSerialId')}
            options={options}
            onChange={handleChange}
            error={!!errors.equipmentSerialId}
            placeholder={translations.tickets.serialPlaceholder}
          />
        </Styled.TicketDeviceInput>

        <Styled.TicketDeviceLinkContainer>
          <Styled.TicketDeviceLink {...getLinkPropsFromPrismicLink(layout.findSerialNumber)} target="_blank">
            {translations.signUp.findSerialNumber}
          </Styled.TicketDeviceLink>
        </Styled.TicketDeviceLinkContainer>
      </Styled.TicketDeviceContent>

      {O.isSome(error) ? (
        <Styled.TicketDeviceErrorMessage>{getLabelBuilder(error, translations)}</Styled.TicketDeviceErrorMessage>
      ) : null}

      <Styled.TicketDeviceActions>
        <Styled.TicketDeviceButton type="submit" $loading={loading}>
          {translations.tickets.next}
        </Styled.TicketDeviceButton>

        {O.isSome(error) ? getLinkBuilder(error, translations) : null}
      </Styled.TicketDeviceActions>
    </form>
  );
};

export default TicketDeviceForm;
